import axiosInstance from '../config/axios';

/**
 * Poll a validation job until completion
 */
const pollJobUntilDone = async (jobId, { intervalMs = 5000, timeoutMs = 15 * 60 * 1000 } = {}) => {
  const start = Date.now();

  while (true) {
    const elapsed = Date.now() - start;
    if (elapsed > timeoutMs) {
      throw new Error('Job polling timed out. Please try again later.');
    }

    try {
      const statusResponse = await axiosInstance.get(`/validation/jobs/${jobId}`);
      const job = statusResponse.data?.job;

      if (!job) {
        throw new Error('Job not found while polling');
      }

      if (job.status === 'completed') {
        return job.result;
      }

      if (job.status === 'failed') {
        const message = job.error || 'Job failed';
        throw new Error(message);
      }
    } catch (err) {
      // For transient errors, retry until timeout
      console.warn(`Job poll error (jobId=${jobId}):`, err.message);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
};

/**
 * Start an async validation job and wait for completion.
 */
const startJobAndWait = async (path, payload, options = {}) => {
  const { intervalMs, timeoutMs, timeout } = options;

  const startResponse = await axiosInstance.post(path, payload, {
    // Keep the initial request short; long work happens async
    timeout: timeout || 30000,
  });

  const jobId = startResponse.data?.jobId;
  if (!jobId) {
    throw new Error('Job did not return an id');
  }

  return pollJobUntilDone(jobId, { intervalMs, timeoutMs });
};

/**
 * Test Neo4j connection
 */
export const testNeo4jConnection = async () => {
  try {
    const response = await axiosInstance.get('/validation/test-connection');
    return response.data;
  } catch (error) {
    console.error('Error testing Neo4j connection:', error);
    throw error;
  }
};

/**
 * Get expected facts from Knowledge Graph
 * @param {string} studyId - Study/Project ID
 */
export const getExpectedFacts = async (studyId) => {
  try {
    const response = await axiosInstance.post('/validation/expected-facts', {
      studyId
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching expected facts:', error);
    throw error;
  }
};

/**
 * Validate protocol sections against Knowledge Graph
 * @param {string} studyId - Study/Project ID
 * @param {string} projectId - MongoDB Project ID
 * @param {Array} sections - Protocol sections to validate
 */
export const validateSections = async (studyId, projectId, sections) => {
  try {
    const response = await axiosInstance.post('/validation/validate-sections', {
      studyId,
      projectId,
      sections
    });

    return response.data;
  } catch (error) {
    console.error('❌ Error validating sections:', error);
    
    // Enhanced error handling
    if (error.response) {
      // Server responded with error
      throw new Error(error.response.data.message || 'Validation failed');
    } else if (error.request) {
      // Request made but no response
      throw new Error('No response from server. Please check if the server is running.');
    } else {
      // Something else happened
      throw new Error(error.message || 'Validation request failed');
    }
  }
};

/**
 * Execute custom Cypher query (for debugging)
 * @param {string} query - Cypher query
 * @param {object} params - Query parameters
 */
export const executeCustomQuery = async (query, params = {}) => {
  try {
    const response = await axiosInstance.post('/validation/query', {
      query,
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error executing custom query:', error);
    throw error;
  }
};

/**
 * Sync protocol sections to Knowledge Graph
 * @param {string} studyId - Study/Project ID
 * @param {string} projectId - MongoDB Project ID
 * @param {string} studyName - Study name
 * @param {Array} sections - Protocol sections to sync
 */
export const syncToKnowledgeGraph = async (studyId, projectId, studyName, sections) => {
  try {
    const response = await axiosInstance.post('/validation/sync-to-knowledge-graph', {
      studyInfo: {
        studyId,
        studyName,
        projectId,
        exportDate: new Date().toISOString(),
        totalSections: sections.length
      },
      sections
    });

    return response.data;
  } catch (error) {
    console.error('❌ Error syncing to Knowledge Graph:', error);
    
    // Enhanced error handling
    if (error.response) {
      throw new Error(error.response.data.message || 'Sync failed');
    } else if (error.request) {
      throw new Error('No response from server. Please check if the server is running.');
    } else {
      throw new Error(error.message || 'Sync request failed');
    }
  }
};

/**
 * Sync protocol sections to Knowledge Graph with Vector Embeddings
 * @param {string} studyId - Study/Project ID
 * @param {string} projectId - MongoDB Project ID
 * @param {string} studyName - Study name
 * @param {Array} sections - Protocol sections to sync
 * @param {number} similarityThreshold - Similarity threshold for semantic relationships (default: 0.7)
 */
export const syncToKnowledgeGraphWithEmbeddings = async (studyId, projectId, studyName, sections, similarityThreshold = 0.7) => {
  try {
    // Kick off async job and poll for completion to avoid HTTP timeouts
    const result = await startJobAndWait(
      '/validation/sync-to-knowledge-graph-with-embeddings-async',
      {
        studyInfo: {
          studyId,
          studyName,
          projectId,
          exportDate: new Date().toISOString(),
          totalSections: sections.length
        },
        sections,
        similarityThreshold,
        // Signal backend to skip base KG write if already synced
        skipBaseSync: true
      },
      {
        intervalMs: 5000,
        timeoutMs: 15 * 60 * 1000 // 15 minutes safety cap
      }
    );

    return result;
  } catch (error) {
    console.error('❌ Error syncing to Knowledge Graph with embeddings:', error);
    
    // Enhanced error handling
    if (error.response) {
      throw new Error(error.response.data.message || 'Sync with embeddings failed');
    } else if (error.request) {
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Embeddings sync timed out. This operation can take several minutes. Please try again or check server logs.');
      }
      throw new Error('No response from server. The embeddings processing may still be running. Please check server logs.');
    } else {
      throw new Error(error.message || 'Sync with embeddings request failed');
    }
  }
};

/**
 * Extract entities from Knowledge Graph Content nodes
 * @param {string} projectId - MongoDB Project ID
 * @param {Array} sectionIds - Optional array of specific section IDs to process
 */
export const extractEntities = async (projectId, sectionIds = null) => {
  try {
    const result = await startJobAndWait(
      '/validation/extract-entities-async',
      {
        projectId,
        sectionIds
      },
      {
        intervalMs: 4000,
        timeoutMs: 10 * 60 * 1000 // 10 minutes safety cap
      }
    );

    return result;
  } catch (error) {
    console.error('❌ Error extracting entities:', error);
    
    if (error.response) {
      throw new Error(error.response.data.message || 'Entity extraction failed');
    } else if (error.request) {
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Entity extraction timed out. This operation can take several minutes. Please try again or check server logs.');
      }
      throw new Error('No response from server. The entity extraction may still be running. Please check server logs.');
    } else {
      throw new Error(error.message || 'Entity extraction request failed');
    }
  }
};

/**
 * Extract standard entities from Knowledge Graph Content nodes
 * Uses ONLY the 60+ standard entity types (StudyIdentifier, TreatmentArm, etc.)
 * @param {string} projectId - MongoDB Project ID
 * @param {Array} sectionIds - Optional array of specific section IDs to process
 * @param {number} concurrency - Optional concurrency level (default: 5)
 */
export const extractStandardEntities = async (projectId, sectionIds = null, concurrency = 5) => {
  try {
    const result = await startJobAndWait(
      '/validation/extract-standard-entities-async',
      {
        projectId,
        sectionIds,
        concurrency
      },
      {
        intervalMs: 4000,
        timeoutMs: 10 * 60 * 1000 // 10 minutes safety cap
      }
    );

    return result;
  } catch (error) {
    console.error('❌ Error extracting standard entities:', error);
    
    if (error.response) {
      throw new Error(error.response.data.message || 'Standard entity extraction failed');
    } else if (error.request) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Standard entity extraction timed out. This operation can take several minutes. Please try again or check server logs.');
      }
      throw new Error('No response from server. The standard entity extraction may still be running. Please check server logs.');
    } else {
      throw new Error(error.message || 'Standard entity extraction request failed');
    }
  }
};

/**
 * AI validations: Gemini semantic checks + Knowledge Graph consistency.
 * Rule-based checks run on the client (see utils/aiValidation.js).
 */
export const runAiValidations = async ({ sections, projectId = null, studyId = null }) => {
  try {
    const response = await axiosInstance.post(
      '/validation/ai-validations',
      { sections, projectId, studyId },
      { timeout: 120000 }
    );
    return response.data;
  } catch (error) {
    console.error('❌ Error running AI validations:', error);
    if (error.response) {
      throw new Error(error.response.data?.message || 'AI validation failed');
    }
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      throw new Error('AI validation timed out. Try again or run rule-based checks only.');
    }
    throw new Error(error.message || 'AI validation request failed');
  }
};

export default {
  testNeo4jConnection,
  getExpectedFacts,
  validateSections,
  executeCustomQuery,
  syncToKnowledgeGraph,
  syncToKnowledgeGraphWithEmbeddings,
  extractEntities,
  extractStandardEntities,
  runAiValidations,
};

